/**
 * Lazy KaTeX rendering. The markdown pipeline emits math as
 * `code.language-math` / `.math-display` nodes carrying raw TeX; we render them
 * here on demand so the ~275KB KaTeX bundle (and its CSS + fonts) only loads for
 * documents that actually contain math — mirroring the mermaid approach.
 */
let katexPromise: Promise<typeof import('katex').default> | null = null;

async function loadKatex() {
  if (!katexPromise) {
    katexPromise = Promise.all([
      import('katex'),
      // CSS + web fonts load with the same chunk (deferred until first math).
      import('katex/dist/katex.min.css'),
    ]).then(([m]) => m.default);
  }
  return katexPromise;
}

const MATH_SELECTOR = 'code.language-math, .math-inline, .math-display';

/** Does `root` contain any unrendered math? (cheap check before importing KaTeX) */
export function hasMath(root: ParentNode): boolean {
  return root.querySelector(MATH_SELECTOR) != null;
}

/** Render every math node inside `root` with KaTeX. No-op if there is none. */
export async function renderKatex(root: HTMLElement): Promise<void> {
  const nodes = Array.from(root.querySelectorAll<HTMLElement>(MATH_SELECTOR));
  if (nodes.length === 0) return;

  const katex = await loadKatex();
  for (const el of nodes) {
    const display = el.classList.contains('math-display');
    const tex = el.textContent ?? '';
    const target = document.createElement(display ? 'div' : 'span');
    if (display) target.className = 'katex-display';
    try {
      katex.render(tex, target, { displayMode: display, throwOnError: false });
    } catch {
      target.textContent = tex;
    }
    el.replaceWith(target);
  }
}
